import React, { useEffect, useState, useRef } from 'react';
import { useApp } from '../../context/AppContext';
import axios from 'axios';
import { toast } from 'sonner';

const MONTHS_TR = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
const MONTHS_EN = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function AdminCalendar() {
  const { apiHeaders, language, t, BACKEND_URL } = useApp();
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState({ blocked_dates: [], booked_dates: [] });
  const [loading, setLoading] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('block');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [showRoomSelect, setShowRoomSelect] = useState(false);
  const roomSelectRef = useRef(null);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const months = language === 'tr' ? MONTHS_TR : MONTHS_EN;
  const days = language === 'tr' ? ['Pzt','Sal','Çar','Per','Cum','Cmt','Paz'] : ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/rooms`).then(res => {
      setRooms(res.data);
      if (res.data.length > 0) setSelectedRoom(res.data[0]._id);
    });
  }, [BACKEND_URL]);

  useEffect(() => {
    if (!selectedRoom) return;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/availability/${selectedRoom}?year=${year}&month=${month + 1}`)
      .then(res => { setAvailability(res.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedRoom, year, month, BACKEND_URL]);

  useEffect(() => {
    const handler = (e) => { if (roomSelectRef.current && !roomSelectRef.current.contains(e.target)) setShowRoomSelect(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const startOffset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date(); today.setHours(0,0,0,0);

  const cells = Array.from({ length: Math.ceil((startOffset + daysInMonth) / 7) * 7 }, (_, i) => {
    const d = i - startOffset + 1;
    return d >= 1 && d <= daysInMonth ? d : null;
  });

  const dateStr = (d) => {
    const dd = String(d).padStart(2,'0');
    const mm = String(month + 1).padStart(2,'0');
    return `${year}-${mm}-${dd}`;
  };

  const isPast = (d) => new Date(year, month, d) < today;
  const isBlocked = (d) => availability.blocked_dates.includes(dateStr(d));
  const isBooked = (d) => availability.booked_dates.includes(dateStr(d));
  const isSelected = (d) => selectedDates.includes(dateStr(d));
  const isToday = (d) => new Date(year, month, d).toDateString() === today.toDateString();

  const toggleDate = (d) => {
    if (isPast(d) || isBooked(d)) return;
    const ds = dateStr(d);
    setSelectedDates(prev => prev.includes(ds) ? prev.filter(x => x !== ds) : [...prev, ds]);
  };

  const prevMonth = () => { const d = new Date(currentMonth); d.setMonth(d.getMonth()-1); setCurrentMonth(d); };
  const nextMonth = () => { const d = new Date(currentMonth); d.setMonth(d.getMonth()+1); setCurrentMonth(d); };

  const getDayStyle = (d) => {
    if (!d) return { background: 'transparent', border: 'none', cursor: 'default', color: 'transparent' };
    if (isPast(d)) return { background: '#F9F7F4', color: '#C8C4BC', cursor: 'not-allowed', border: '1px solid #EFE7DA', opacity: 0.5 };
    if (isBooked(d)) return { background: '#fef3c7', color: '#92400e', cursor: 'default', border: '1px solid #fde68a', fontWeight: 700 };
    if (isBlocked(d)) return { background: '#fee2e2', color: '#991b1b', cursor: 'pointer', border: '1px solid #fca5a5', fontWeight: 700 };
    if (isSelected(d)) return { background: '#1F3A2E', color: 'white', cursor: 'pointer', border: '1px solid #1F3A2E', fontWeight: 700 };
    if (isToday(d)) return { background: 'rgba(31,58,46,0.08)', color: '#1F3A2E', cursor: 'pointer', border: '2px solid #1F3A2E', fontWeight: 700 };
    return { background: '#dcfce7', color: '#166534', cursor: 'pointer', border: '1px solid #bbf7d0' };
  };

  const handleBlockAction = (mode) => {
    if (selectedDates.length === 0) { toast.error(language === 'tr' ? 'Tarih seçin' : 'Select dates'); return; }
    setDialogMode(mode); setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (dialogMode === 'block') {
        await axios.post(`${BACKEND_URL}/api/blocked-dates`, { room_id: selectedRoom, dates: selectedDates, reason: reason || 'Admin block', source: 'manual' }, apiHeaders());
        toast.success(`${selectedDates.length} ${language === 'tr' ? 'tarih bloke edildi' : 'dates blocked'}`);
      } else {
        await axios.delete(`${BACKEND_URL}/api/blocked-dates`, { ...apiHeaders(), data: { room_id: selectedRoom, dates: selectedDates } });
        toast.success(`${selectedDates.length} ${language === 'tr' ? 'bloke kaldırıldı' : 'dates unblocked'}`);
      }
      setSelectedDates([]); setReason(''); setDialogOpen(false);
      const res = await axios.get(`${BACKEND_URL}/api/availability/${selectedRoom}?year=${year}&month=${month+1}`);
      setAvailability(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  const selectedRoomName = rooms.find(r => r._id === selectedRoom)?.name || (language === 'tr' ? 'Oda seçin' : 'Select room');

  return (
    <div style={{ fontFamily: 'Manrope, sans-serif' }}>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '28px', fontWeight: 600, color: '#1F3A2E', marginBottom: '4px' }}>{t('calendar')}</h1>
        <p style={{ fontSize: '13px', color: '#6E7F5B' }}>{language === 'tr' ? 'Oda müsaitlik takvimi ve tarih bloklama' : 'Room availability calendar and date blocking'}</p>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {/* Room selector */}
        <div ref={roomSelectRef} style={{ position: 'relative' }}>
          <button
            data-testid="admin-calendar-room-select"
            onClick={() => setShowRoomSelect(!showRoomSelect)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 14px', background: 'white', border: '1.5px solid #D8D1C6', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#1F3A2E', fontFamily: 'Manrope, sans-serif', minWidth: '180px', justifyContent: 'space-between' }}
          >
            <span>{selectedRoomName}</span>
            <span style={{ fontSize: '10px', color: '#6E7F5B' }}>▼</span>
          </button>
          {showRoomSelect && (
            <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, background: 'white', border: '1px solid #D8D1C6', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', zIndex: 100, minWidth: '200px', overflow: 'hidden' }}>
              {rooms.map(room => (
                <button key={room._id} onClick={() => { setSelectedRoom(room._id); setShowRoomSelect(false); setSelectedDates([]); }}
                  style={{ width: '100%', textAlign: 'left', padding: '10px 14px', background: selectedRoom === room._id ? '#F0FAF0' : 'transparent', color: selectedRoom === room._id ? '#1F3A2E' : '#3A3632', fontSize: '13px', fontWeight: selectedRoom === room._id ? 700 : 400, border: 'none', cursor: 'pointer', fontFamily: 'Manrope, sans-serif' }}
                  onMouseEnter={e => { if (selectedRoom !== room._id) e.currentTarget.style.background = '#F6F1E7'; }}
                  onMouseLeave={e => { if (selectedRoom !== room._id) e.currentTarget.style.background = 'transparent'; }}
                >
                  {room.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Month nav */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button onClick={prevMonth} style={{ width: '32px', height: '32px', borderRadius: '8px', border: '1px solid #D8D1C6', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', color: '#1F3A2E' }}>‹</button>
          <span style={{ fontSize: '14px', fontWeight: 700, color: '#1F3A2E', minWidth: '120px', textAlign: 'center' }}>{months[month]} {year}</span>
          <button onClick={nextMonth} style={{ width: '32px', height: '32px', borderRadius: '8px', border: '1px solid #D8D1C6', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', color: '#1F3A2E' }}>›</button>
        </div>

        {/* Action buttons */}
        {selectedDates.length > 0 && (
          <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: '#6E7F5B' }}>{selectedDates.length} {language === 'tr' ? 'seçili' : 'selected'}</span>
            <button onClick={() => handleBlockAction('block')} style={{ background: '#dc2626', color: 'white', padding: '7px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
              🔒 {language === 'tr' ? 'Bloke Et' : 'Block'}
            </button>
            <button onClick={() => handleBlockAction('unblock')} style={{ background: '#166534', color: 'white', padding: '7px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
              🔓 {language === 'tr' ? 'Bloke Kaldır' : 'Unblock'}
            </button>
            <button onClick={() => setSelectedDates([])} style={{ background: 'transparent', color: '#6E7F5B', padding: '7px 10px', borderRadius: '8px', border: '1px solid #D8D1C6', cursor: 'pointer', fontSize: '12px', fontFamily: 'Manrope, sans-serif' }}>✕</button>
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
        {[
          { bg: '#dcfce7', border: '#bbf7d0', color: '#166534', label: language === 'tr' ? 'Müsait' : 'Available' },
          { bg: '#fef3c7', border: '#fde68a', color: '#92400e', label: language === 'tr' ? 'Rezervasyonlu' : 'Booked' },
          { bg: '#fee2e2', border: '#fca5a5', color: '#991b1b', label: language === 'tr' ? 'Bloke' : 'Blocked' },
          { bg: '#1F3A2E', border: '#1F3A2E', color: 'white', label: language === 'tr' ? 'Seçili' : 'Selected' },
        ].map(item => (
          <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#5A5450' }}>
            <div style={{ width: '16px', height: '16px', borderRadius: '4px', background: item.bg, border: `1px solid ${item.border}` }} />
            {item.label}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div style={{ background: 'white', border: '1px solid #EFE7DA', borderRadius: '16px', padding: '20px', opacity: loading ? 0.7 : 1 }}>
        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '8px' }}>
          {days.map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: '12px', fontWeight: 700, color: '#6E7F5B', padding: '6px 0' }}>{d}</div>
          ))}
        </div>
        {/* Date cells */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
          {cells.map((d, idx) => {
            const style = getDayStyle(d);
            return (
              <button
                key={idx}
                data-testid={d ? 'admin-calendar-day-cell' : undefined}
                onClick={() => d && toggleDate(d)}
                style={{
                  height: '40px', borderRadius: '8px', fontSize: '13px', fontWeight: style.fontWeight || 400,
                  cursor: style.cursor, background: style.background, color: style.color,
                  border: style.border || 'none', fontFamily: 'Manrope, sans-serif',
                  transition: 'transform 0.1s',
                }}
                onMouseEnter={e => { if (d && !isPast(d) && !isBooked(d)) e.currentTarget.style.transform = 'scale(1.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                {d || ''}
              </button>
            );
          })}
        </div>
      </div>

      {/* Block/Unblock Modal - Custom overlay */}
      {dialogOpen && (
        <div onClick={(e) => { if (e.target === e.currentTarget) setDialogOpen(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: 'white', borderRadius: '20px', width: '100%', maxWidth: '400px', padding: '24px', fontFamily: 'Manrope, sans-serif' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontFamily: 'Cormorant Garamond, serif', color: '#1F3A2E', fontSize: '20px', fontWeight: 600 }}>
                {dialogMode === 'block' ? (language === 'tr' ? '🔒 Tarihleri Bloke Et' : '🔒 Block Dates') : (language === 'tr' ? '🔓 Bloke Kaldır' : '🔓 Unblock Dates')}
              </h2>
              <button onClick={() => setDialogOpen(false)} style={{ background: '#F6F1E7', border: 'none', width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', color: '#6E7F5B' }}>✕</button>
            </div>
            <p style={{ fontSize: '14px', color: '#5A5450', marginBottom: '14px' }}>
              {selectedDates.length} {language === 'tr' ? 'tarih' : 'date(s)'} {dialogMode === 'block' ? (language === 'tr' ? 'bloke edilecek' : 'will be blocked') : (language === 'tr' ? 'bloke kaldırılacak' : 'will be unblocked')}
            </p>
            {dialogMode === 'block' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '5px' }}>
                  {language === 'tr' ? 'Sebep (opsiyonel)' : 'Reason (optional)'}
                </label>
                <textarea
                  data-testid="admin-calendar-block-dialog-submit"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder={language === 'tr' ? 'Bakım, özel etkinlik...' : 'Maintenance, private event...'}
                  style={{ width: '100%', border: '1.5px solid #D8D1C6', borderRadius: '8px', minHeight: '70px', fontSize: '13px', padding: '8px 10px', fontFamily: 'Manrope, sans-serif', boxSizing: 'border-box', outline: 'none' }}
                />
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                data-testid="admin-calendar-block-dialog-submit"
                onClick={handleSave}
                disabled={saving}
                style={{ flex: 1, background: dialogMode === 'block' ? '#dc2626' : '#166534', color: 'white', padding: '12px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}
              >
                {saving ? '...' : dialogMode === 'block' ? (language === 'tr' ? 'Bloke Et' : 'Block') : (language === 'tr' ? 'Bloke Kaldır' : 'Unblock')}
              </button>
              <button onClick={() => setDialogOpen(false)} style={{ padding: '12px 18px', background: 'white', border: '1px solid #D8D1C6', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontFamily: 'Manrope, sans-serif' }}>
                {language === 'tr' ? 'İptal' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
